import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { releaseDeviceOnLogout } from "@/lib/push";
import { clearAcquisition, getAcquisition } from "@/lib/acquisition";
import { settleLangParam } from "@/lib/lang";

const AuthContext = createContext(null);

/** "es-US" | "es" -> "es"; anything else -> "en". The two values the column allows. */
function baseLanguage(tag) {
  return String(tag || "").toLowerCase().startsWith("es") ? "es" : "en";
}

export function AuthProvider({ children }) {
  const { i18n } = useTranslation();
  const [user, setUser] = useState(null);
  // Platform feature flags, public and session-independent. Starts empty so a
  // gated surface reads falsy before the fetch lands; `loading` below keeps the
  // gated routes from rendering (and redirecting) until it has.
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      // T23. Before setUser: the profile sync below must never see a `?lang=`
      // that only anonymous visitors are meant to get.
      settleLangParam(true);
      setUser(data.user || data);
    } catch (e) {
      settleLangParam(false);
      setUser(null);
    }
  }, []);

  const loadFeatures = useCallback(async () => {
    try {
      const { data } = await api.get("/platform/features");
      setFeatures(data || {});
    } catch (e) {
      // A failed fetch leaves every flag off. Better to hide a live feature
      // than to render a surface the backend will reject.
      setFeatures({});
    }
  }, []);

  useEffect(() => {
    // Both must settle before `loading` clears: a route gated on a flag would
    // otherwise bounce the user to their dashboard on the first paint.
    Promise.allSettled([checkAuth(), loadFeatures()]).then(() => setLoading(false));
  }, [checkAuth, loadFeatures]);

  /**
   * Keep `user_profiles.language` in step with the language being read (DY1.6).
   *
   * The column existed since DY1.1 and **nothing ever wrote it.** The EN/ES
   * switch is i18next with a localStorage detector — entirely client-side — so
   * every account sat at the default `en` no matter what language its owner was
   * using the site in. That was invisible until voice shipped, because voice is
   * the first thing that reads it: a call opens in the profile's language, so
   * Spanish-speaking users were greeted in English by a Spanish-voiced agent.
   *
   * Syncing rather than only writing on the toggle is deliberate. The detector
   * picks Spanish straight from the browser for a Spanish user who never
   * touches the switch, and that person is exactly the one this is for — they
   * would otherwise stay on the default forever precisely because the site
   * already looked right to them.
   *
   * Fire-and-forget: this is a preference following the interface, and a failed
   * PATCH means the next page load tries again. Nothing waits on it.
   */
  useEffect(() => {
    if (!user?.id && !user?.user_id) return;
    const stored = user?.profile?.language;
    const showing = baseLanguage(i18n.language);
    if (!stored || stored === showing) return;
    api.patch("/users/me", { language: showing })
      .then(() => setUser((current) => (current ? {
        ...current,
        profile: { ...(current.profile || {}), language: showing },
      } : current)))
      .catch(() => { /* try again next load */ });
  }, [user, i18n.language]);

  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data;
  };

  const signupEmail = async (email, password, full_name,
                             { anonymousKey, role, ...profile } = {}) => {
    // The new profile starts in the language this page is being read in, rather
    // than in the column's `en` default. See `_signup_language` in routes/auth.
    //
    // `anonymousKey` is only ever sent by Dany's inline registration (DY2.1).
    // The backend uses it to look up, in its own tables, whether a job post is
    // waiting behind the verification link — which decides the wording of the
    // email, not anything about the account.
    //
    // T22. `acquisition` is the first-touch channel the browser kept (UTMs or an
    // external referrer, 30 days), sent when there is one. The server stores it
    // at creation only and drops it silently if it is malformed; it is cleared
    // here once the account exists so it cannot be attributed twice.
    //
    // `role` is required by the server since 2026-10-28 — the page the form is
    // on decides it — and `profile` is what that page asked for: a ZIP, and
    // for a FixyBee the phone (captured, not verified) and one category.
    const acquisition = getAcquisition();
    const { data } = await api.post("/auth/signup", {
      email, password, full_name, language: baseLanguage(i18n.language),
      role: role || "client", ...profile,
      ...(acquisition ? { acquisition } : {}),
    }, anonymousKey ? { headers: { "X-Dany-Anonymous-Key": anonymousKey } } : undefined);
    clearAcquisition();
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    // First, while the session still exists: the device leaves this account's
    // push audience (AUDIT.md 11, finding 41). Never throws, capped at 4s.
    await releaseDeviceOnLogout();
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setUser(null);
  };

  const refresh = checkAuth;

  return (
    <AuthContext.Provider value={{ user, features, loading, loginEmail, signupEmail, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
